import { formatCurrency } from '../format/currency.js';
import type { InvoiceOutcome } from '../types/enums.js';
import type { InvoicePaymentState } from './invoice-payment.js';
import { round2, sumMoney } from './money.js';

/**
 * Partner commission - the referral and commission agreement with independent IT consultants.
 * See docs/partner/1-12-Partneraftale-Designnotat.md.
 *
 * A partner who referred a sale gets a share of what the customer is invoiced on it: one rate on
 * development hours, another on running services (hosting, licences, operations). The rates are
 * a snapshot on the opportunity in CRM, copied from the partner agreement by flow 7.22, so a new
 * agreement never changes an old sale.
 *
 * Commission is only due on what the customer has **paid**. Everything here is a preview: nothing
 * is marked as settled yet - that is the monthly run with Henrik's approval, on its own page.
 */

/** The partner agreement's status in CRM - `cr19f_aftalestatus`: Udkast, Aktiv, Opsagt, Ophørt. */
export const PARTNER_AGREEMENT_STATUSES = ['draft', 'active', 'terminated', 'ended'] as const;
export type PartnerAgreementStatus = (typeof PARTNER_AGREEMENT_STATUSES)[number];

/** A partner agreement - one row in `cr19f_partneraftale`. */
export interface PartnerAgreement {
  id: string;
  /** `PA-1000` - CRM's autonumber starts at 1000. The key the statement is written under. */
  agreement_no: string | null;
  /** The partner's account in CRM. */
  partner: { id: string; name: string } | null;
  /** Receives the statements. */
  contact: { id: string; name: string; email: string | null } | null;
  /** Null when CRM has a status Codepass does not know - `status_label` still says it. */
  status: PartnerAgreementStatus | null;
  /** CRM's own label for the status. */
  status_label: string | null;
  /** Ikrafttrædelse - the date of the last signature (§ 9.1). */
  effective_date: string | null;
  /** Opsigelse modtaget. */
  notice_received_date: string | null;
  /** Ophør pr. - the end of the month three months after the notice (§ 9.3). */
  end_date: string | null;
  /** The signed agreement on SharePoint. */
  signed_agreement_url: string | null;
  /** The last check of the partner's CVR registration (§ 6.1). */
  cvr_checked_date: string | null;
}

/**
 * Why a chosen agreement could not be read:
 *
 *   forbidden   Codepass' application user has no read privilege on the table in CRM - its
 *               security role, not the column security profile
 *   failed      anything else
 */
export type PartnerAgreementError = 'forbidden' | 'failed';

/**
 * The referral on one contract: the partner fields on the opportunity. Only exists when the sale
 * is marked "Henvist via partner" - a contract that is not referred has none.
 */
export interface PartnerReferral {
  /** The agreement the sale was referred under. Null when the salesperson left it empty. */
  agreement_id: string | null;
  /**
   * The rates, in percent, as flow 7.22 copied them to the opportunity. **Null is not 0 %**: it
   * means the flow did not run, or Codepass' application user cannot read the column-secured
   * fields - see `partnerReferralProblems`.
   */
  hours_rate_pct: number | null;
  recurring_rate_pct: number | null;
  /** Null when the agreement could not be read, or none is chosen. */
  agreement: PartnerAgreement | null;
  /** Why `agreement` is null although one is chosen. Null otherwise. */
  agreement_error: PartnerAgreementError | null;
}

/**
 * What a referral is missing before commission can be worked out, in Danish. Empty when nothing
 * is missing.
 *
 * A missing rate is an error, never 0 % (design note § 6.3): the sale is referred, so the partner
 * is owed something - the number is just not there.
 */
export function partnerReferralProblems(
  referral: PartnerReferral,
  contractNo: string | null,
): string[] {
  const on = contractNo ? ` på ${contractNo}` : '';
  const problems: string[] = [];

  if (!referral.agreement_id) {
    problems.push(`Salget er markeret som henvist, men der er ikke valgt en partneraftale${on}.`);
  } else if (!referral.agreement) {
    problems.push(
      referral.agreement_error === 'forbidden'
        ? `Partneraftalen${on} kan ikke læses: Codepass har ikke Læs på tabellen Partneraftale i ` +
            'sin sikkerhedsrolle i CRM.'
        : `Partneraftalen${on} kunne ikke læses fra CRM.`,
    );
  }

  const missing = [
    referral.hours_rate_pct === null ? 'udviklingstimer' : null,
    referral.recurring_rate_pct === null ? 'løbende ydelser' : null,
  ].filter((value): value is string => value !== null);

  if (missing.length > 0) {
    problems.push(
      `Sats mangler${on} (${missing.join(' og ')}). Satserne skrives af flow 7.22, når ` +
        'partneraftalen vælges på salgsmuligheden. Er de udfyldt i CRM, mangler Codepass ' +
        'læseadgang til de kolonnesikrede felter.',
    );
  }

  return problems;
}

/**
 * What an invoice line is, for its rate and for the statement.
 *
 *   hours       development hours - the development rate
 *   recurring   operations, hosting and licences - the running-services rate
 *   other       everything else: a fixed-price app, a workshop, a line without an item - the
 *               development rate
 *
 * **Everything the customer is invoiced earns commission** (decided 06-10-2026, the design note's
 * open point § 10.1). Only running services earn the lower rate. 'hours' and 'other' earn the same;
 * they are kept apart because the statement shows hours on the one and not on the other.
 */
export type CommissionLineType = 'hours' | 'recurring' | 'other';

/** The development hours item in BC - `Udviklingstimer`. */
export const COMMISSION_HOURS_ITEM_NO = 'V0001';

/**
 * Running services: the BC items whose name starts with one of these.
 *
 * By name and not by BC's product posting group: `Applikations drift - Lille` and `- Enterprise`
 * are in the posting group CONSULTING while `- Standard` and `- Business` are in SUBSCRIPTIONS
 * (checked 06-10-2026), so the group would split one product in two. The same prefixes as the
 * wall screen's "faste ydelser" - see `RECURRING_PRODUCT_PREFIXES` in the API.
 */
export const COMMISSION_RECURRING_ITEM_PREFIXES = ['applikations drift', 'hosting', 'licens'];

/** What an invoice line is, read from its BC item - see `CommissionLineType`. */
export function commissionLineType(item: {
  number: string | null;
  name: string | null;
}): CommissionLineType {
  if (item.number?.trim().toUpperCase() === COMMISSION_HOURS_ITEM_NO) return 'hours';

  const name = item.name?.trim().toLowerCase() ?? '';
  if (COMMISSION_RECURRING_ITEM_PREFIXES.some((prefix) => name.startsWith(prefix))) {
    return 'recurring';
  }
  return 'other';
}

/** The rate a line type earns under a referral. Null when the opportunity is missing it. */
export function commissionRate(
  lineType: CommissionLineType,
  referral: Pick<PartnerReferral, 'hours_rate_pct' | 'recurring_rate_pct'>,
): number | null {
  return lineType === 'recurring' ? referral.recurring_rate_pct : referral.hours_rate_pct;
}

/**
 * Where the invoice behind a line is in payment.
 *
 *   paid               the customer has paid it in full
 *   awaiting_payment   posted and not (fully) paid - partly paid counts as unpaid (§ 7.2)
 *   credited           cancelled by a credit memo; earns nothing
 *   partly_credited    closed, but partly by a credit memo; earns nothing until someone has looked
 *                      at it - the credit memo's lines cannot be read, so the part that was paid
 *                      is not known line by line (§ 7.2, F17)
 *   unresolved         closed in BC without payments that explain it; earns nothing until someone
 *                      has looked at it
 */
export type CommissionPayment =
  'paid' | 'awaiting_payment' | 'credited' | 'partly_credited' | 'unresolved';

/**
 * The payment of one posted invoice, from the customer ledger when it knows the invoice and from
 * the invoice's own status otherwise. Null when neither says anything.
 *
 * **The ledger first**, because BC's own `Paid` only means closed: an invoice closed by a credit
 * memo is `Paid` too (see `invoicePaymentState`), and commission on a credited invoice would be
 * paid out on money that never came in. The ledger can tell the two apart - see `settleInvoices`.
 *
 * `creditedAmount` is the part of a paid invoice the ledger settled with a credit memo rather
 * than a payment. Any of it makes the invoice `partly_credited`: commission on the full lines would
 * be paid on money that was given back.
 */
export function commissionPayment(
  outcome: InvoiceOutcome | null,
  state: InvoicePaymentState | null,
  creditedAmount = 0,
): CommissionPayment | null {
  switch (outcome) {
    case 'paid':
      return creditedAmount > 0.005 ? 'partly_credited' : 'paid';
    case 'credited':
      return 'credited';
    case 'unresolved':
      return 'unresolved';
    case 'open':
    case 'overdue':
      return 'awaiting_payment';
    default:
      break;
  }

  if (state === 'paid') return 'paid';
  if (state === 'partly_paid' || state === 'unpaid') return 'awaiting_payment';
  return null;
}

/** Why a line earns no commission. */
export type CommissionExclusion =
  'rate_missing' | 'credited' | 'partly_credited' | 'unresolved' | 'after_end';

/** One posted invoice on the contract, with the lines that belong to the contract. */
export interface CommissionInvoice {
  invoice_id: string;
  /** BC's number - the legal invoice number. */
  invoice_no: string | null;
  invoice_date: string | null;
  payment: CommissionPayment;
  /** Estimated from the customer ledger. Null unless paid. */
  paid_date: string | null;
  lines: Array<{
    /** BC's id of the line - the key a settled line is remembered by. */
    line_id: string;
    description: string;
    item_no: string | null;
    item_name: string | null;
    quantity: number;
    amount_excl_vat: number;
  }>;
}

/** The statement a line was settled on. */
export interface CommissionSettlement {
  statement_no: string;
  settled_at: string;
}

export interface PartnerCommissionLine {
  invoice_id: string;
  invoice_no: string | null;
  invoice_date: string | null;
  payment: CommissionPayment;
  paid_date: string | null;
  line_id: string;
  description: string;
  item_no: string | null;
  line_type: CommissionLineType;
  /** Hours on a development hours line; the quantity of anything else. */
  quantity: number;
  amount_excl_vat: number;
  /** Null when the opportunity is missing the rate. */
  rate_pct: number | null;
  /** Null when the line earns nothing - see `excluded`. */
  commission_excl_vat: number | null;
  excluded: CommissionExclusion | null;
  /** Set when the line has been paid out to the partner. It is never settled twice. */
  settlement: CommissionSettlement | null;
}

export interface PartnerCommissionSummary {
  /** Commission on paid lines not settled yet - what the next statement would pay. */
  ready_excl_vat: number;
  /** Commission on lines whose invoice is not paid yet. */
  awaiting_excl_vat: number;
  /** Commission already paid out on earlier statements. */
  settled_excl_vat: number;
}

export interface PartnerCommission {
  lines: PartnerCommissionLine[];
  summary: PartnerCommissionSummary;
}

/** A line the next statement would pay: paid, earning, and not settled before. */
export function isReadyCommissionLine(line: PartnerCommissionLine): boolean {
  return line.payment === 'paid' && line.commission_excl_vat !== null && line.settlement === null;
}

/** The three totals over a set of lines - one contract, or every contract under an agreement. */
export function commissionSummary(
  lines: readonly PartnerCommissionLine[],
): PartnerCommissionSummary {
  const earned = (keep: (line: PartnerCommissionLine) => boolean): number =>
    sumMoney(lines.filter(keep).map((line) => line.commission_excl_vat ?? 0));

  return {
    ready_excl_vat: earned(isReadyCommissionLine),
    awaiting_excl_vat: earned(
      (line) => line.payment === 'awaiting_payment' && line.commission_excl_vat !== null,
    ),
    settled_excl_vat: earned((line) => line.settlement !== null),
  };
}

/**
 * Commission on every line of the contract's posted invoices.
 *
 * Worked out on the line amount excluding VAT (§ 5.1), rounded to øre per line. A line earns
 * nothing when:
 *
 *   - its rate is missing on the opportunity (§ 6.3);
 *   - its invoice was credited, partly credited, or closed without a payment that explains it;
 *   - it is a running service invoiced after the agreement's end date (§ 9.4: running services
 *     only up to and including "Ophør pr.").
 *
 * A line in `settled` keeps its commission and carries its statement - it stays on the contract's
 * tab, but no statement pays it again.
 *
 * Lines with no amount - text lines - are left out: there is nothing to earn on them.
 */
export function partnerCommission(
  invoices: readonly CommissionInvoice[],
  referral: Pick<PartnerReferral, 'hours_rate_pct' | 'recurring_rate_pct'> & {
    agreement: Pick<PartnerAgreement, 'end_date'> | null;
  },
  settled: ReadonlyMap<string, CommissionSettlement> = new Map(),
): PartnerCommission {
  const endDate = referral.agreement?.end_date ?? null;
  const lines: PartnerCommissionLine[] = [];

  for (const invoice of invoices) {
    for (const line of invoice.lines) {
      if (line.amount_excl_vat === 0) continue;

      const lineType = commissionLineType({ number: line.item_no, name: line.item_name });
      const rate = commissionRate(lineType, referral);

      const excluded: CommissionExclusion | null =
        rate === null
          ? 'rate_missing'
          : invoice.payment === 'credited'
            ? 'credited'
            : invoice.payment === 'partly_credited'
              ? 'partly_credited'
              : invoice.payment === 'unresolved'
                ? 'unresolved'
                : lineType === 'recurring' &&
                    endDate !== null &&
                    invoice.invoice_date !== null &&
                    invoice.invoice_date > endDate
                  ? 'after_end'
                  : null;

      lines.push({
        invoice_id: invoice.invoice_id,
        invoice_no: invoice.invoice_no,
        invoice_date: invoice.invoice_date,
        payment: invoice.payment,
        paid_date: invoice.paid_date,
        line_id: line.line_id,
        description: line.description,
        item_no: line.item_no,
        line_type: lineType,
        quantity: round2(line.quantity),
        amount_excl_vat: round2(line.amount_excl_vat),
        rate_pct: rate,
        commission_excl_vat:
          excluded === null && rate !== null ? round2((line.amount_excl_vat * rate) / 100) : null,
        excluded,
        settlement: settled.get(line.line_id.toLowerCase()) ?? null,
      });
    }
  }

  return { lines, summary: commissionSummary(lines) };
}

/**
 * The contract a line on a BC invoice belongs to.
 *
 * An invoice can cover several of the customer's agreements - `ONC-1134+ONC-1124/HOURS` - and the
 * commission belongs to the referred one only (design note B7). In order:
 *
 *   1. the contract Codepass stored for the line when it created the invoice;
 *   2. the only contract the reference names;
 *   3. the contract number the line text starts with - `ONC-1134 · Serviceaftale`, as
 *      `contractLineLabel` writes it.
 *
 * Null when none of them says - the line is then not counted on any contract, rather than on a
 * guessed one.
 */
export function commissionLineContractNo(
  line: { description: string; stored_contract_no?: string | null },
  referenceContractNos: readonly string[],
): string | null {
  const stored = line.stored_contract_no?.trim();
  if (stored) return stored;

  if (referenceContractNos.length === 1) return referenceContractNos[0] ?? null;

  const text = line.description.trim().toUpperCase();
  return (
    referenceContractNos.find((no) => {
      const wanted = no.trim().toUpperCase();
      return text === wanted || text.startsWith(`${wanted} `) || text.startsWith(`${wanted}·`);
    }) ?? null
  );
}

/** The response of `GET /contracts/:id/partner-commission`. */
export interface PartnerCommissionResponse extends PartnerCommission {
  /** Null when the contract is not referred by a partner. */
  referral: PartnerReferral | null;
  /** Danish. What is missing on the referral - see `partnerReferralProblems`. */
  problems: string[];
  /** Danish. Caveats about the sources, e.g. that the customer ledger could not be read. */
  notes: string[];
}

/* ------------------------------------------------------------------------------------------ */
/* Statements - the settlement on /fakturering/provision                                       */
/* ------------------------------------------------------------------------------------------ */

/**
 * The statement number: the agreement number and the month it was approved in -
 * `PA-0001-2026-10` (design note § 7.5). A second statement on the same agreement in the same
 * month gets `-2`, a third `-3`. The partner writes it on their invoice to OneCode.
 *
 * Without an agreement number - the column is an autonumber, so only for a row created before it
 * - the first eight characters of the agreement's id stand in for it.
 */
export function commissionStatementNo(
  agreement: { id: string; agreement_no: string | null },
  approvedOn: string,
  taken: readonly string[],
): string {
  const base = commissionStatementPrefix(agreement, approvedOn);
  const used = new Set(taken.map((no) => no.trim().toUpperCase()));
  if (!used.has(base.toUpperCase())) return base;

  for (let suffix = 2; ; suffix += 1) {
    const candidate = `${base}-${suffix}`;
    if (!used.has(candidate.toUpperCase())) return candidate;
  }
}

/** `PA-0001-2026-10` without a suffix - what every statement of the month starts with. */
export function commissionStatementPrefix(
  agreement: { id: string; agreement_no: string | null },
  approvedOn: string,
): string {
  const key = agreement.agreement_no?.trim() || `PA-${agreement.id.slice(0, 8).toUpperCase()}`;
  return `${key}-${approvedOn.slice(0, 7)}`;
}

/** `Provisionsopgoerelse-PA-0001-2026-10.xlsx` - ASCII, so every mail client keeps the name. */
export function commissionStatementFileName(statementNo: string): string {
  return `Provisionsopgoerelse-${statementNo.replace(/[^A-Za-z0-9-]/g, '')}.xlsx`;
}

/**
 * The email a statement goes out with - the fields set on /fakturering/provision. The subject and
 * the text are templates: `{partner}`, `{kontaktperson}`, `{opgørelse}`, `{aftalenummer}` and
 * `{beløb}` are filled in per statement - see `renderCommissionTemplate`.
 *
 * The sender is not here: it is `PARTNER_COMMISSION_SENDER` on /admin/integrationer - see
 * `PartnerCommissionOverview.email_sender`.
 */
export interface CommissionEmailSettings {
  /** Always copied - Henrik, per the design note § 7.4. */
  cc: string[];
  subject: string;
  body: string;
}

export const COMMISSION_EMAIL_PLACEHOLDERS = [
  'partner',
  'kontaktperson',
  'opgørelse',
  'aftalenummer',
  'beløb',
] as const;

export type CommissionEmailPlaceholder = (typeof COMMISSION_EMAIL_PLACEHOLDERS)[number];

/**
 * What each placeholder becomes in the email the partner reads - listed under Mailopsætning, so
 * whoever writes the template knows. Danish: it is on the page. Keep in step with `emailDraft` in
 * apps/api/src/domain/partner-settlement.ts, which fills them in.
 */
export const COMMISSION_EMAIL_PLACEHOLDER_HELP: Record<
  CommissionEmailPlaceholder,
  { description: string; example: string }
> = {
  partner: {
    description: 'Partnerens navn, som det står på partneraftalen i CRM.',
    example: 'Kristjansen Invest ApS',
  },
  kontaktperson: {
    description:
      'Navnet på partneraftalens kontaktperson — den, mailen sendes til. Partnerens navn, hvis ' +
      'aftalen ingen kontaktperson har.',
    example: 'Henrik Kristjansen',
  },
  opgørelse: {
    description:
      'Opgørelsens nummer: aftalenummer og måned, med -2, -3 ved flere i samme måned. Partneren ' +
      'skriver det som reference på sin faktura til OneCode.',
    example: 'PA-1000-2026-10',
  },
  aftalenummer: {
    description: 'Partneraftalens nummer i CRM.',
    example: 'PA-1000',
  },
  beløb: {
    description: 'Provisionen i alt på opgørelsen, ekskl. moms.',
    example: formatCurrency(1245.5),
  },
};

/** Customer-facing, so Danish. The starting point until someone saves their own. */
export const DEFAULT_COMMISSION_EMAIL: CommissionEmailSettings = {
  cc: [],
  subject: 'Provisionsopgørelse {opgørelse} fra OneCode',
  body:
    'Hej {kontaktperson}\n\n' +
    'Hermed provisionsopgørelsen {opgørelse} under partneraftale {aftalenummer}. Provisionen ' +
    'udgør {beløb} ekskl. moms.\n\n' +
    'Opgørelsen er vedhæftet som Excel. Send venligst en faktura på beløbet til OneCode, og ' +
    'angiv {opgørelse} som reference.\n\n' +
    'Venlig hilsen\nOneCode ApS',
};

/** The template with its placeholders filled in. An unknown `{word}` is left as it is. */
export function renderCommissionTemplate(
  template: string,
  values: Record<CommissionEmailPlaceholder, string>,
): string {
  return template.replace(/\{([^{}]+)\}/g, (match, name: string) =>
    (COMMISSION_EMAIL_PLACEHOLDERS as readonly string[]).includes(name)
      ? values[name as CommissionEmailPlaceholder]
      : match,
  );
}

/** A line on a statement - the commission line with the contract it belongs to. */
export interface PartnerStatementLine extends PartnerCommissionLine {
  contract_no: string;
  customer_name: string | null;
}

/** A referred contract under an agreement, with what is missing on it. */
export interface PartnerOverviewContract {
  id: string;
  contract_no: string | null;
  title: string;
  customer_name: string | null;
  hours_rate_pct: number | null;
  recurring_rate_pct: number | null;
  /** Danish - see `partnerReferralProblems`. */
  problems: string[];
}

/** The email a statement would go out with, filled in from the settings. */
export interface CommissionEmailDraft {
  to: string[];
  cc: string[];
  subject: string;
  body: string;
}

/**
 * One partner agreement on /fakturering/provision: its referred contracts and their lines.
 *
 * A referral without an agreement - the salesperson left the lookup empty - is its own group
 * with `agreement_id` null. It can show its lines, but it cannot be settled: there is no partner
 * to pay.
 */
export interface PartnerOverviewGroup {
  agreement_id: string | null;
  agreement: PartnerAgreement | null;
  contracts: PartnerOverviewContract[];
  lines: PartnerStatementLine[];
  summary: PartnerCommissionSummary;
  /** The lines an approval now would settle - see `isReadyCommissionLine`. */
  ready_line_ids: string[];
  /** The statement number an approval now would get. Null when it cannot be settled. */
  next_statement_no: string | null;
  /** The email it would go out with. Null when it cannot be settled. */
  email: CommissionEmailDraft | null;
}

/** A statement that has been approved - and, unless `send_error` says otherwise, sent. */
export interface PartnerStatement {
  id: string;
  statement_no: string;
  agreement_id: string;
  agreement_no: string | null;
  partner_name: string;
  total_commission_excl_vat: number;
  line_count: number;
  approved_at: string;
  approved_by: string | null;
  sent_at: string | null;
  sent_to: string[];
  /** Danish. Why the last send failed. Null once it has gone out. */
  send_error: string | null;
}

/**
 * The send filter on `GET /partner-commission/statements`: 'sent' is out without an error since,
 * 'not_sent' is never out or failed on the last try - what "Send igen" is for.
 */
export const PARTNER_STATEMENT_SEND_FILTERS = ['sent', 'not_sent'] as const;
export type PartnerStatementSendFilter = (typeof PARTNER_STATEMENT_SEND_FILTERS)[number];

/** `GET /partner-commission`. */
export interface PartnerCommissionOverview {
  /** False while the partner fields do not exist in CRM - then nothing is referred. */
  crm_ready: boolean;
  /** False while the settlement tables have not been created - then nothing can be approved. */
  settlement_ready: boolean;
  groups: PartnerOverviewGroup[];
  /*
   * The approved statements are not here: they are their own paged list,
   * `GET /partner-commission/statements` - see `PartnerStatementSendFilter`.
   */
  /**
   * The mailbox the statements are sent from - `PARTNER_COMMISSION_SENDER` on
   * /admin/integrationer. Null while it is not set: then nothing can be sent.
   */
  email_sender: string | null;
  email_settings: CommissionEmailSettings;
  /** Danish. Caveats about the sources. */
  notes: string[];
}
